import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  InputNumber,
  Modal,
  Row,
  Segmented,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { ReloadOutlined, SendOutlined, TeamOutlined } from '@ant-design/icons';
import { adminApi } from '@/services/api';
import { apiErrorMessage, apiErrorStatus } from '@/utils/apiError';
import {
  cameBackRate,
  canEditCap,
  groupVariantsBySegment,
  isTutorialRow,
  variantLabel,
} from '@/utils/lifecyclePush';
import type {
  LifecyclePushRun,
  LifecyclePushStatsRow,
  LifecyclePushStatus,
  LifecyclePushTriggerStatus,
  LifecyclePushVariantRow,
  LifecycleTriggerKey,
} from '@/types';

const { Text } = Typography;

const TRIGGER_LABELS: Record<LifecycleTriggerKey, string> = {
  ftue_done_no_match: 'Tutorial done, no match',
  first_match_unfinished: 'First match unfinished',
  inactive_d1: 'Inactive 1 day',
  inactive_d3: 'Inactive 3 days',
  inactive_d7: 'Inactive 7 days',
  ftue_incomplete: 'Left mid-tutorial',
};

/** Days the results cover; the backend takes 1 to 30. */
const DAY_OPTIONS = [1, 7, 14, 30];
const DEFAULT_DAYS = 7;

const DEFAULT_SAMPLE = 10;

const label = (key: LifecycleTriggerKey) => TRIGGER_LABELS[key] ?? key;
const num = (n: number) => (n ?? 0).toLocaleString();

/** The outcome counts a stats row and a copy row share; all are of delivered pushes. */
interface Outcomes {
  delivered: number;
  returned24h: number;
  /** Absent from an older API: shown as a dash, never as a zero. */
  returned2h?: number;
  matchStarted24h?: number;
  returnedNoMatchPush24h?: number;
  tutorialDone24h?: number;
}

/** "12 (3.4%)": the count and its share of delivered pushes; a dash when the count is missing. */
const withRate = (n: number | undefined, delivered: number) =>
  n === undefined || n === null ? '—' : `${num(n)} (${cameBackRate(n, delivered)})`;

/**
 * Delivered and the came-back columns for a stats row or a copy row.
 * `showTutorial` says whether Tutorial done applies to a row (a copy row of the
 * tutorial trigger or segment; every per-day row); otherwise it shows a dash.
 */
function outcomeColumns<T extends Outcomes>(showTutorial: (r: T) => boolean) {
  const rate =
    (field: keyof Omit<Outcomes, 'delivered'>, strong = false) =>
    (_: unknown, r: T) => {
      const text = withRate(r[field], r.delivered);
      return strong && text !== '—' ? <Text strong>{text}</Text> : text;
    };
  return [
    {
      title: 'Delivered',
      dataIndex: 'delivered',
      key: 'delivered',
      align: 'right' as const,
      render: (n: number) => <Text strong>{num(n)}</Text>,
    },
    {
      title: 'Back 2 h',
      key: 'returned2h',
      align: 'right' as const,
      render: rate('returned2h'),
    },
    {
      title: 'Back 24 h',
      key: 'returned24h',
      align: 'right' as const,
      render: rate('returned24h', true),
    },
    {
      title: 'Match 24 h',
      key: 'matchStarted24h',
      align: 'right' as const,
      render: rate('matchStarted24h'),
    },
    {
      title: (
        <Tooltip title="Came back within 24 h and no match push was sent to them in between">
          Back, no match push
        </Tooltip>
      ),
      key: 'returnedNoMatchPush24h',
      align: 'right' as const,
      render: rate('returnedNoMatchPush24h'),
    },
    {
      title: (
        <Tooltip title="Finished the tutorial within 24 h; the key figure for the tutorial pushes (Left mid-tutorial and the tutorial copy of the inactive triggers)">
          Tutorial done
        </Tooltip>
      ),
      key: 'tutorialDone24h',
      align: 'right' as const,
      render: (_: unknown, r: T) => {
        const text = showTutorial(r) ? withRate(r.tutorialDone24h, r.delivered) : '—';
        return text === '—' ? text : <Text strong>{text}</Text>;
      },
    },
  ];
}
const istTime = (iso: string) =>
  `${new Date(iso).toLocaleString('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })} IST`;

/** Result shown next to a trigger after "Count now" or "Send sample". */
interface TriggerNote {
  count?: { qualified: number; skippedLocal: number };
  sample?: string;
}

/**
 * Live Ops → Lifecycle Pushes: the Automatic sending switch and daily cap,
 * one row per trigger with a sample send, the per-day results and the recent
 * sends. Delivered and came back within 24 h are the go/no-go numbers, so they
 * come first.
 */
export const LifecyclePushesTab: React.FC = () => {
  const [status, setStatus] = useState<LifecyclePushStatus | null>(null);
  const [stats, setStats] = useState<LifecyclePushStatsRow[]>([]);
  const [variants, setVariants] = useState<LifecyclePushVariantRow[]>([]);
  // The backend has no /variants yet (the release roll): no toast, a note instead.
  const [variantsMissing, setVariantsMissing] = useState(false);
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [bySegment, setBySegment] = useState(true);
  const [runs, setRuns] = useState<LifecyclePushRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  // Bumped to reload the results for the same period (Refresh, after a sample).
  const [resultsTick, setResultsTick] = useState(0);
  const [saving, setSaving] = useState(false);
  const [capInput, setCapInput] = useState<number | null>(null);
  const [sampleCounts, setSampleCounts] = useState<Partial<Record<LifecycleTriggerKey, number>>>(
    {}
  );
  const [notes, setNotes] = useState<Partial<Record<LifecycleTriggerKey, TriggerNote>>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const showStatus = (next: LifecyclePushStatus) => {
    setStatus(next);
    setCapInput(next.dailyCap);
  };

  /** Status and recent sends; not the day-picker results, and it leaves the cap input to showStatus. */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        adminApi.getLifecyclePushStatus(),
        adminApi.getLifecyclePushRuns(50),
      ]);
      showStatus(s.data);
      setRuns(Array.isArray(r.data) ? r.data : []);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to load lifecycle pushes'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // The per-day and per-copy results for the chosen period. A slower, older
  // response must not overwrite a newer one, so a changed period cancels this one.
  useEffect(() => {
    let cancelled = false;
    setResultsLoading(true);
    (async () => {
      let missing = false;
      const [st, v] = await Promise.all([
        adminApi.getLifecyclePushStats(days).catch((error) => {
          if (!cancelled) message.error(apiErrorMessage(error, 'Failed to load results'));
          return null;
        }),
        // The copy table must not take the per-day results down with it.
        adminApi.getLifecyclePushVariants(days).catch((error) => {
          if (apiErrorStatus(error) === 404) missing = true;
          else if (!cancelled) {
            message.error(apiErrorMessage(error, 'Failed to load results by copy'));
          }
          return null;
        }),
      ]);
      if (cancelled) return;
      setStats(Array.isArray(st?.data) ? st.data : []);
      setVariants(Array.isArray(v?.data) ? v.data : []);
      setVariantsMissing(missing);
      setResultsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [days, resultsTick]);

  const refresh = () => {
    load();
    setResultsTick((t) => t + 1);
  };

  const save = async (body: { dailyCap?: number; autoEnabled?: boolean }, done: string) => {
    setSaving(true);
    try {
      const response = await adminApi.updateLifecyclePushSettings(body);
      showStatus(response.data);
      message.success(done);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to save'));
      load();
    } finally {
      setSaving(false);
    }
  };

  const onSwitch = (on: boolean) => {
    if (!status) return;
    if (!on) {
      save({ autoEnabled: false }, 'Automatic sending is off');
      return;
    }
    Modal.confirm({
      title: 'Switch automatic sending on?',
      content: `Lifecycle pushes go out every 10 minutes from 09:00 to 22:00 IST, paced through the day, up to ${num(
        status.dailyCap
      )} today (${num(status.usedToday)} used).`,
      okText: 'Switch on',
      onOk: () => save({ autoEnabled: true }, 'Automatic sending is on'),
    });
  };

  const onSaveCap = () => {
    if (!status || capInput === null) return;
    const next = capInput;
    const apply = () => save({ dailyCap: next }, `Daily cap set to ${num(next)}`);
    if (next <= status.dailyCap) {
      apply();
      return;
    }
    Modal.confirm({
      title: `Raise the daily cap to ${num(next)}?`,
      content: `Up to ${num(next)} lifecycle pushes a day (now ${num(
        status.dailyCap
      )}), automatic and samples together.`,
      okText: 'Raise',
      onOk: apply,
    });
  };

  const countNow = async (key: LifecycleTriggerKey) => {
    setBusy(`count:${key}`);
    try {
      const response = await adminApi.countLifecyclePushTrigger(key);
      setNotes((n) => ({ ...n, [key]: { ...n[key], count: response.data } }));
    } catch (error) {
      message.error(apiErrorMessage(error, 'Count failed'));
    } finally {
      setBusy(null);
    }
  };

  const sendSample = (trigger: LifecyclePushTriggerStatus) => {
    const count = sampleCounts[trigger.key] ?? DEFAULT_SAMPLE;
    Modal.confirm({
      title: `Send a sample of ${label(trigger.key)}?`,
      content: `Pushes "${trigger.title}" now to up to ${count} random players who qualify, under every rule an automatic run follows. They count against today's cap.`,
      okText: 'Send sample',
      onOk: async () => {
        setBusy(`sample:${trigger.key}`);
        try {
          const response = await adminApi.sendLifecyclePushSample(trigger.key, count);
          const one = response.data.perTrigger[trigger.key];
          const text = one
            ? `Sent to ${num(response.data.claimed)} of ${count} asked (${num(
                one.qualified
              )} qualified, ${num(one.skippedLocal)} skipped for local, ${num(
                one.skippedCap
              )} over the cap)`
            : `Sent to ${num(response.data.claimed)} of ${count} asked`;
          setNotes((n) => ({ ...n, [trigger.key]: { ...n[trigger.key], sample: text } }));
          message.success(text);
          refresh();
        } catch (error) {
          message.error(apiErrorMessage(error, 'Sample failed'));
        } finally {
          setBusy(null);
        }
      },
    });
  };

  const triggerColumns = [
    {
      title: 'Trigger',
      key: 'trigger',
      render: (_: unknown, t: LifecyclePushTriggerStatus) => (
        <Space direction="vertical" size={0}>
          <Text strong>{label(t.key)}</Text>
          <Text type="secondary">{t.key}</Text>
          {t.earliestIstHour > 9 && <Text type="secondary">from {t.earliestIstHour}:00 IST</Text>}
        </Space>
      ),
    },
    {
      title: 'Push',
      key: 'push',
      render: (_: unknown, t: LifecyclePushTriggerStatus) => (
        <Space direction="vertical" size={0}>
          <Text strong>{t.title}</Text>
          <Text>{t.body}</Text>
        </Space>
      ),
    },
    {
      title: 'Latest run',
      key: 'latest',
      render: (_: unknown, t: LifecyclePushTriggerStatus) => {
        const note = notes[t.key]?.count;
        return (
          <Space direction="vertical" size={4}>
            {t.latest ? (
              <Text>
                {num(t.latest.qualified)} qualified, {num(t.latest.skippedLocal)} skipped for local{' '}
                <Text type="secondary">({istTime(t.latest.at)})</Text>
              </Text>
            ) : (
              <Text type="secondary">No automatic run yet</Text>
            )}
            {note && (
              <Text>
                Now: {num(note.qualified)} qualified, {num(note.skippedLocal)} skipped for local
              </Text>
            )}
            <Button
              size="small"
              icon={<TeamOutlined />}
              loading={busy === `count:${t.key}`}
              disabled={busy !== null && busy !== `count:${t.key}`}
              onClick={() => countNow(t.key)}
            >
              Count now
            </Button>
          </Space>
        );
      },
    },
    {
      title: 'Sample',
      key: 'sample',
      render: (_: unknown, t: LifecyclePushTriggerStatus) => (
        <Space direction="vertical" size={4}>
          <Space>
            <InputNumber
              min={1}
              max={100}
              precision={0}
              value={sampleCounts[t.key] ?? DEFAULT_SAMPLE}
              onChange={(v) => setSampleCounts((c) => ({ ...c, [t.key]: v ?? DEFAULT_SAMPLE }))}
              style={{ width: 80 }}
            />
            <Button
              icon={<SendOutlined />}
              loading={busy === `sample:${t.key}`}
              disabled={busy !== null && busy !== `sample:${t.key}`}
              onClick={() => sendSample(t)}
            >
              Send sample
            </Button>
          </Space>
          {notes[t.key]?.sample && <Text type="secondary">{notes[t.key]?.sample}</Text>}
        </Space>
      ),
    },
  ];

  const statsColumns = [
    { title: 'IST day', dataIndex: 'istDay', key: 'istDay' },
    {
      title: 'Trigger',
      dataIndex: 'trigger',
      key: 'trigger',
      render: (key: LifecycleTriggerKey) => label(key),
    },
    ...outcomeColumns<LifecyclePushStatsRow>(() => true),
    { title: 'Sent', dataIndex: 'sent', key: 'sent', align: 'right' as const, render: num },
    {
      title: 'No device',
      dataIndex: 'noDevice',
      key: 'noDevice',
      align: 'right' as const,
      render: num,
    },
    { title: 'Failed', dataIndex: 'failed', key: 'failed', align: 'right' as const, render: num },
    {
      title: 'Expired',
      dataIndex: 'expired',
      key: 'expired',
      align: 'right' as const,
      render: num,
    },
    {
      title: 'Dropped (limit)',
      dataIndex: 'limited',
      key: 'limited',
      align: 'right' as const,
      render: num,
    },
    {
      title: 'Qualified',
      dataIndex: 'qualified',
      key: 'qualified',
      align: 'right' as const,
      render: num,
    },
    {
      title: (
        <Tooltip title="Not pushed because the phone's own reminder is due within 2 h">
          Skipped (local)
        </Tooltip>
      ),
      dataIndex: 'skippedLocal',
      key: 'skippedLocal',
      align: 'right' as const,
      render: num,
    },
    {
      title: 'Skipped (cap)',
      dataIndex: 'skippedCap',
      key: 'skippedCap',
      align: 'right' as const,
      render: num,
    },
  ];

  const period = days === 1 ? 'last day' : `last ${days} days`;
  const copyRows = bySegment ? groupVariantsBySegment(variants) : variants;
  const copyColumns = [
    {
      title: 'Trigger',
      dataIndex: 'trigger',
      key: 'trigger',
      render: (key: LifecycleTriggerKey) => label(key),
    },
    {
      title: 'Copy',
      key: 'variant',
      render: (_: unknown, r: LifecyclePushVariantRow) => variantLabel(r.variant, bySegment),
    },
    { title: 'Sent', dataIndex: 'sent', key: 'sent', align: 'right' as const, render: num },
    ...outcomeColumns<LifecyclePushVariantRow>((r) => isTutorialRow(r.trigger, r.variant)),
  ];

  const runColumns = [
    {
      title: 'Time',
      dataIndex: 'startedAt',
      key: 'startedAt',
      render: (iso: string) => istTime(iso),
    },
    {
      title: 'Kind',
      key: 'kind',
      render: (_: unknown, r: LifecyclePushRun) =>
        r.kind === 'sample' ? (
          <Space size={4}>
            <Tag color="purple">SAMPLE</Tag>
            <Text type="secondary">{r.adminEmail}</Text>
          </Space>
        ) : (
          <Tag color="blue">AUTO</Tag>
        ),
    },
    {
      title: 'Triggers',
      dataIndex: 'triggers',
      key: 'triggers',
      render: (keys: LifecycleTriggerKey[]) => keys.map(label).join(', ') || '-',
    },
    {
      title: 'Delivered',
      dataIndex: 'delivered',
      key: 'delivered',
      align: 'right' as const,
      render: (n: number) => <Text strong>{num(n)}</Text>,
    },
    {
      title: 'Came back ≤24 h so far',
      key: 'returned24h',
      align: 'right' as const,
      render: (_: unknown, r: LifecyclePushRun) => (
        <Text strong>
          {num(r.returned24h)} ({cameBackRate(r.returned24h, r.delivered)})
        </Text>
      ),
    },
    {
      title: 'Players',
      dataIndex: 'claimed',
      key: 'claimed',
      align: 'right' as const,
      render: num,
    },
    { title: 'Failed', dataIndex: 'failed', key: 'failed', align: 'right' as const, render: num },
    {
      title: 'Cancelled',
      dataIndex: 'cancelled',
      key: 'cancelled',
      align: 'right' as const,
      render: num,
    },
  ];

  const capChanged = status !== null && capInput !== null && capInput !== status.dailyCap;
  const capAllowed =
    status !== null && capInput !== null && canEditCap(capInput, status.dailyCap, status.canRaise);

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        title="Automatic sending"
        extra={
          <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading || resultsLoading}>
            Refresh
          </Button>
        }
        loading={loading && !status}
      >
        {status && (
          <Row gutter={[24, 16]}>
            <Col xs={24} md={8}>
              <Space direction="vertical" size={4}>
                <Space>
                  <Switch
                    checked={status.autoEnabled}
                    loading={saving}
                    disabled={!status.autoEnabled && !status.canRaise}
                    onChange={onSwitch}
                  />
                  <Text strong>{status.autoEnabled ? 'On' : 'Off'}</Text>
                </Space>
                <Text type="secondary">
                  {status.autoEnabled && status.nextRunAt
                    ? `Next run ${istTime(status.nextRunAt)}`
                    : 'No automatic runs while off. Samples still work.'}
                </Text>
                {!status.autoEnabled && !status.canRaise && (
                  <Text type="secondary">Only a super admin can switch it on.</Text>
                )}
              </Space>
            </Col>
            <Col xs={24} md={16}>
              <Space direction="vertical" size={4}>
                <Space wrap>
                  <Text strong>Daily cap</Text>
                  <InputNumber
                    min={0}
                    max={status.canRaise ? status.maxDailyCap : status.dailyCap}
                    precision={0}
                    step={1000}
                    value={capInput}
                    onChange={(v) => setCapInput(v)}
                    style={{ width: 140 }}
                  />
                  <Button
                    type="primary"
                    loading={saving}
                    disabled={!capChanged || !capAllowed}
                    onClick={onSaveCap}
                  >
                    Save cap
                  </Button>
                </Space>
                <Text>
                  Used <Text strong>{num(status.usedToday)}</Text> of {num(status.dailyCap)} today
                  (IST), automatic and samples together.
                </Text>
                {!status.canRaise && (
                  <Text type="secondary">
                    You can lower the cap; only a super admin can raise it (up to{' '}
                    {num(status.maxDailyCap)}).
                  </Text>
                )}
              </Space>
            </Col>
          </Row>
        )}
      </Card>

      <Card title="Triggers">
        <Table
          dataSource={status?.triggers ?? []}
          columns={triggerColumns}
          rowKey="key"
          loading={loading}
          pagination={false}
          size="small"
        />
      </Card>

      <Card
        title={`Results per IST day (${period})`}
        extra={
          <Segmented
            value={days}
            onChange={(v) => setDays(Number(v))}
            options={DAY_OPTIONS.map((d) => ({ value: d, label: d === 1 ? '1 day' : `${d} days` }))}
          />
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Delivered and came back within 24 h are the go/no-go numbers."
          description="Came back = the player started a session within 2 h or 24 h of delivery; the % is of delivered. Today's figure is incomplete until 24 h have passed. Qualified and skipped count distinct players and are approximate. Dropped (limit) = not sent: the player already had 2 pushes in the last 10 minutes or 10 today."
        />
        <Table
          dataSource={stats}
          columns={statsColumns}
          rowKey={(r) => `${r.istDay}:${r.trigger}`}
          loading={resultsLoading}
          pagination={false}
          size="small"
          scroll={{ x: true }}
          locale={{ emptyText: 'No lifecycle pushes in this period' }}
        />
      </Card>

      <Card
        title={`Results by copy (${period})`}
        extra={
          <Space>
            <Text>By segment (all languages)</Text>
            <Switch checked={bySegment} onChange={setBySegment} />
          </Space>
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Came back counts every return after the push, including players who would have come back
          anyway; compare copies with each other.
        </Text>
        {variantsMissing && (
          <Text type="warning" style={{ display: 'block', marginBottom: 16 }}>
            Results by copy need the newer backend.
          </Text>
        )}
        <Table
          dataSource={copyRows}
          columns={copyColumns}
          rowKey={(r) => `${r.trigger}|${r.variant}|${r.language}`}
          loading={resultsLoading}
          pagination={false}
          size="small"
          scroll={{ x: true }}
          locale={{ emptyText: 'No lifecycle pushes in this period' }}
        />
      </Card>

      <Card title="Recent sends (last 50)">
        <Table
          dataSource={runs}
          columns={runColumns}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
          scroll={{ x: true }}
          locale={{ emptyText: 'No samples or automatic sends yet' }}
        />
      </Card>
    </Space>
  );
};

export default LifecyclePushesTab;
